import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { HasTeamSwitch } from "@/components/pros/HasTeamSwitch";
import { ProTrades } from "@/components/pros/ProTrades";
import { ProPhotos, PortfolioSwitch } from "@/components/pros/ProPhotos";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useCollection } from "@/hooks/useCollection";
import { AllFieldsPanel } from "@/components/explorer/AllFieldsPanel";
import { getClusters } from "@/services/ccApi";
import { adapter } from "@/services/db";
import { useAuth } from "@/context/AuthContext";
import { text, list, inr, fmtDate, fmtTs, initials } from "@/lib/format";
import { OnboardingsByDate } from "@/components/pros/OnboardingsByDate";
import { LoginAttempts } from "@/components/pros/LoginAttempts";
import { AccountSuspension } from "@/components/accounts/AccountSuspension";
import { DateRangeFilter, useRange, initialRange } from "@/components/shared/DateRangeFilter";
import { inRange } from "@/lib/dateRange";
import { proCompletion } from "@/lib/proCompletion";
import { kycUrls } from "@/lib/kyc";
import { EditRecordButton } from "@/components/explorer/RecordEditor";
import { BulkBar, SelectAllBox, SelectBox, useSelection, ActionDialog } from "@/components/shared/BulkSelect";
import { NewDot, HealthChip, NewActivityBar, useRowSignals } from "@/components/shared/RowSignals";
import { healthOf } from "@/lib/health";
import { bucketOf, BUCKETS, BUCKET_LABEL, OLD_TAB, proActions, proName } from "@/lib/proStatus";
import { cn } from "@/lib/utils";
import { ProSignups } from "@/components/pros/ProSignups";
import { tradesOfPro } from "@/components/catalog/ProCategoriesTab";

const DATE_FIELDS = [{ key: "created_at", label: "Onboarded on" }, { key: "updated_at", label: "Last updated" }];
const areaOf = (p) => list(p.service_cities?.length ? p.service_cities : p.cities, ", ", text(p.city));
const BUCKET_TONE = { approval: "text-indigo-700", correction: "text-amber-700", signing_up: "text-slate-600", active: "text-emerald-700", on_hold: "text-slate-600", suspended: "text-red-700", rejected: "text-slate-500" };

function Completion({ p, showMissing }) {
  const c = proCompletion(p);
  return (
    <div className="min-w-[140px]">
      <div className="flex items-center gap-2"><Progress value={c.pct} className="h-1.5 w-20" /><span className="text-xs font-mono">{c.pct}%</span></div>
      {showMissing && c.missing.length > 0 && <p className="text-[10px] text-amber-800 mt-0.5 max-w-[220px] truncate" title={c.missing.join(", ")}>Missing: {c.missing.join(", ")}</p>}
    </div>
  );
}

// Columns that matter for the tab you're on (no repeated data).
const EXTRA = {
  approval: { head: ["Profile", "Submitted"], cells: (p) => [<Completion p={p} showMissing />, <span className="text-xs text-muted-foreground">{fmtTs(p.submitted_at || p.updated_at)}</span>] },
  correction: { head: ["What we asked", "Asked on"], cells: (p) => [<span className="text-xs max-w-[260px] block truncate" title={p.correction_note || ""}>{text(p.correction_note)}</span>, <span className="text-xs text-muted-foreground">{fmtTs(p.reviewed_at)}</span>] },
  signing_up: { head: ["Profile", "Joined"], cells: (p) => [<Completion p={p} showMissing />, <span className="text-xs text-muted-foreground">{fmtDate(p.created_at)}</span>] },
  active: { head: ["Cluster", "Rating · jobs", "Profile"], cells: (p) => [
    p.cluster ? <span className="text-xs rounded-full border border-primary/20 bg-primary/10 text-primary px-2 py-0.5">{text(p.cluster)}</span> : <span className="text-xs text-muted-foreground">—</span>,
    <span className="font-mono text-xs">★ {Number(p.rating_average || 0).toFixed(1)} · {p.completed_project_count || 0}</span>, <span className="font-mono text-xs">{proCompletion(p).pct}%</span>] },
  on_hold: { head: ["Why on hold", "Since"], cells: (p) => [<span className="text-xs max-w-[260px] block truncate" title={p.hold_reason || ""}>{text(p.hold_reason)}</span>, <span className="text-xs text-muted-foreground">{fmtTs(p.held_at)}</span>] },
  suspended: { head: ["They see", "Since"], cells: (p) => [<span className="text-xs max-w-[260px] block truncate" title={p.suspension_user_message || ""}>{text(p.suspension_user_message)}</span>, <span className="text-xs text-muted-foreground">{fmtTs(p.suspended_at)}</span>] },
  rejected: { head: ["Reason", "On"], cells: (p) => [<span className="text-xs max-w-[260px] block truncate" title={p.correction_note || ""}>{text(p.correction_note)}</span>, <span className="text-xs text-muted-foreground">{fmtTs(p.reviewed_at)}</span>] },
  all: { head: ["Status", "Profile"], cells: (p) => [<span className={cn("text-xs font-medium", BUCKET_TONE[bucketOf(p)])}>{BUCKET_LABEL[bucketOf(p)]}</span>, <span className="font-mono text-xs">{proCompletion(p).pct}%</span>] },
};
// One obvious next step per tab, right on the row.
const QUICK = { approval: "approve", correction: null, signing_up: null, active: null, on_hold: "release", suspended: null, rejected: "reopen", all: null };

export default function Professionals() {
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab") || "approval";
  const tab = BUCKETS.some((b) => b.key === raw) ? raw : OLD_TAB[raw] || "approval";
  const joining = raw === "joining";
  const logins = raw === "logins";
  const signups = useCollection("pro_signups");
  const newSignups = signups.filter((d) => String(d.status || "new").toLowerCase() === "new").length;
  const pros = useCollection("professionals");
  const { user } = useAuth();
  const [clusters, setClusters] = useState([]);
  useEffect(() => { getClusters().then(setClusters).catch(() => {}); }, []);
  const [openId, setOpenId] = useState(null);
  const [search, setSearch] = useState("");
  const [byDate, setByDate] = useState(raw === "new");
  const [dr, setDr] = useState(initialRange());
  const [one, setOne] = useState(null); // { action, pro }
  const range = useRange(dr);

  const procats = useCollection("professional_categories");
  const svcCats = useCollection("categories");
  const actions = useMemo(() => [...proActions({ clusters }),
    { key: "trades", label: "Add professional category", icon: "HardHat", description: "Adds it to each selected pro's professional categories (keeps the ones they have). They start getting leads for its services in a few seconds.",
      ask: [{ key: "t", label: "Professional category", type: "select", required: true, options: procats.map((t) => [t.id, t.name]) }],
      eligible: () => procats.length > 0, skipNote: "no professional categories yet",
      run: (p, v) => adapter.update("professionals", p.id, { pro_category_ids: [...new Set([...tradesOfPro(p, procats), v.t])], updated_at: new Date().toISOString() }) },
  ], [clusters, procats]);
  const actionBy = Object.fromEntries(actions.map((a) => [a.key, a]));
  const counts = useMemo(() => pros.reduce((m, p) => { const b = bucketOf(p); m[b] = (m[b] || 0) + 1; return m; }, { all: pros.length }), [pros]);
  const urgent = useMemo(() => pros.filter((p) => bucketOf(p) === "approval" && healthOf("professionals", p)).length, [pros]);
  const rows = pros
    .filter((p) => tab === "all" || bucketOf(p) === tab)
    .filter((p) => inRange(p[dr.field], range))
    .filter((p) => (search ? JSON.stringify(p).toLowerCase().includes(search.toLowerCase()) : true))
    .sort((a, b) => String(b.updated_at || b.created_at || "").localeCompare(String(a.updated_at || a.created_at || "")));
  const selected = pros.find((p) => p.id === openId) || null;
  const setCluster = async (p, cluster) => { await adapter.update("professionals", p.id, { cluster, updated_at: new Date().toISOString() }); toast.success(`Cluster set to ${cluster}`); };
  const sig = useRowSignals("professionals", "professionals");
  const pick = useSelection(rows);
  const tabActions = actions.filter((a) => tab === "all" || rows.some((p) => (a.eligible || (() => true))(p)));
  const ex = EXTRA[tab];
  const bucketInfo = BUCKETS.find((b) => b.key === tab);
  const canRun = (a) => (a.roles || ["admin", "ops_manager"]).includes(user?.role);

  const tabsBar = (
      <div className="mb-4 flex flex-wrap gap-1.5" data-testid="pro-tabs">
        <button type="button" onClick={() => { setParams({ tab: "joining" }); pick.clear(); }} data-testid="pro-tab-joining"
          className={cn("flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition-colors", joining ? "border-primary/50 bg-primary/5 font-medium text-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground hover:bg-accent/40")}>
          <Icons.Inbox className={cn("h-4 w-4", joining ? "text-primary" : "")} />Joining requests
          <span className={cn("tabular-nums text-xs rounded-full px-1.5", joining ? "bg-primary/10 text-primary" : "bg-secondary")}>{newSignups}</span>
          {newSignups > 0 && <span className="cc-new-dot" title={`${newSignups} new from the website`} />}
        </button>
        {BUCKETS.map((b) => {
          const Ic = Icons[b.icon] || Icons.Circle;
          const on = !joining && !logins && tab === b.key;
          return (
            <button key={b.key} type="button" onClick={() => { setParams({ tab: b.key }); pick.clear(); }} data-testid={`pro-tab-${b.key}`}
              className={cn("flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition-colors", on ? "border-primary/50 bg-primary/5 font-medium text-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground hover:bg-accent/40")}>
              <Ic className={cn("h-4 w-4", on ? "text-primary" : "")} />{b.label}
              <span className={cn("tabular-nums text-xs rounded-full px-1.5", on ? "bg-primary/10 text-primary" : "bg-secondary")}>{counts[b.key] || 0}</span>
              {b.key === "approval" && urgent > 0 && <span className="cc-new-dot" title={`${urgent} waiting too long`} />}
            </button>
          );
        })}
        <button type="button" onClick={() => { setParams({ tab: "logins" }); pick.clear(); }} data-testid="pro-tab-logins"
          className={cn("flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition-colors", logins ? "border-primary/50 bg-primary/5 font-medium text-foreground" : "border-border bg-card text-muted-foreground hover:text-foreground hover:bg-accent/40")}>
          <Icons.LogIn className={cn("h-4 w-4", logins ? "text-primary" : "")} />Login attempts
        </button>
      </div>
  );
  if (logins) return (
    <div>
      <PageHeader title="Professionals & Field" subtitle="Every Brugo Pro login attempt (pro_logins) — phone, outcome and device, matched to registered professionals." />
      {tabsBar}
      <LoginAttempts pros={pros} onOpen={(id) => { setParams({ tab: "all" }); setOpenId(id); }} />
    </div>
  );
  if (joining) return (
    <div>
      <PageHeader title="Professionals & Field" subtitle="Joining requests from brugo.in (Join as a pro): call, verify and start onboarding." />
      {tabsBar}
      <ProSignups />
    </div>
  );

  return (
    <div>
      <PageHeader title="Professionals & Field" subtitle="Every professional sits in one tab by where they are — approve new ones, keep active ones moving, and handle holds and suspensions. Synced live with the shared `professionals` collection."
        actions={<NewActivityBar section="professionals" colls={["professionals"]} />} />

      {tabsBar}
      <p className="mb-3 text-xs text-muted-foreground" data-testid="pro-tab-hint">{bucketInfo?.hint}</p>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="pro-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone, category, city…" className="pl-9" />
        </div>
        <DateRangeFilter value={dr} onChange={setDr} fields={DATE_FIELDS} testId="pro-date-filter" />
        <label className="flex items-center gap-2 text-xs text-muted-foreground"><Switch checked={byDate} onCheckedChange={setByDate} data-testid="pro-by-date" />Group by sign-up date</label>
        <span className="text-xs text-muted-foreground font-mono ml-auto" data-testid="pro-shown-count">{rows.length} shown</span>
      </div>

      {byDate ? <OnboardingsByDate pros={rows} onOpen={setOpenId} /> : (<>
        <BulkBar s={pick} path="professionals" rows={pros} label="professionals" nameOf={proName} actions={tabActions}
          fields={[{ key: "status", label: "Status" }, { key: "cluster", label: "Cluster" }, { key: "has_team", label: "Has a team", type: "boolean" }, { key: "portfolio_enabled", label: "Portfolio on", type: "boolean" }, { key: "covers_gurgaon", label: "Covers GGN", type: "boolean" }]} />
        <Card>
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow>
              <TableHead className="w-8"><SelectAllBox s={pick} testId="pro-select-all" /></TableHead>
              <TableHead>Professional</TableHead><TableHead>Category</TableHead><TableHead>Service area</TableHead><TableHead>Portfolio</TableHead>
              {ex.head.map((h) => <TableHead key={h}>{h}</TableHead>)}
              <TableHead className="text-right">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((p) => {
                const q = QUICK[tab] && actionBy[QUICK[tab]];
                return (
                  <TableRow key={p.id} className={cn("hover:bg-accent/40", sig.rowClass(p), pick.sel.has(p.id) && "bg-primary/5")} data-testid={`pro-row-${p.id}`}>
                    <TableCell><SelectBox s={pick} id={p.id} /></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar url={p.profile_image_url} name={p.full_name} />
                        <div className="leading-tight min-w-0">
                          <p className="font-medium text-foreground flex items-center gap-1.5"><NewDot show={sig.isNew(p.id)} />{text(p.full_name || p.business_name, "Unnamed")}</p>
                          <p className="text-[11px] text-muted-foreground truncate max-w-[220px]">{[p.business_name && p.business_name !== p.full_name ? p.business_name : "", p.phone].filter(Boolean).join(" · ") || "—"}</p>
                          {healthOf("professionals", p) && <HealthChip h={healthOf("professionals", p)} className="mt-0.5" />}
                          {p.covers_gurgaon && <span className="mt-0.5 inline-block text-[10px] font-medium rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 px-2 py-0.5" data-testid={`covers-ggn-${p.id}`}>Covers GGN</span>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell><p className="text-sm text-foreground">{text(p.category_name || p.professional_type)}</p><p className="text-[11px] text-muted-foreground max-w-[180px] truncate">{list(p.subcategories, ", ", "")}</p></TableCell>
                    <TableCell className="text-muted-foreground text-sm max-w-[160px] truncate">{areaOf(p)}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}><PortfolioSwitch pro={p} /></TableCell>
                    {ex.cells(p).map((c, i) => <TableCell key={i}>{c}</TableCell>)}
                    <TableCell className="text-right whitespace-nowrap">
                      {q && canRun(q) && q.eligible(p) && <Button size="sm" variant="outline" className="h-8 mr-1" onClick={() => setOne({ action: q, pro: p })} data-testid={`quick-${q.key}-${p.id}`}>{q.label}</Button>}
                      <EditRecordButton path="professionals" id={p.id} />
                      <Button variant="ghost" size="sm" onClick={() => { sig.opened(p.id); setOpenId(p.id); }} data-testid={`review-pro-${p.id}`}>Open <Icons.ChevronRight className="h-4 w-4 ml-1" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && <TableRow><TableCell colSpan={6 + ex.head.length} className="text-center py-10 text-muted-foreground">Nobody here right now.</TableCell></TableRow>}
            </TableBody>
          </Table></div>
        </Card>
      </>)}

      <Sheet open={!!selected} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="pro-review-drawer">
          {selected && (
            <>
              <SheetHeader className="text-left">
                <div className="flex items-center gap-3">
                  <Avatar url={selected.profile_image_url} name={selected.full_name} big />
                  <div>
                    <SheetTitle className="font-heading">{text(selected.full_name || selected.business_name, "Unnamed")}</SheetTitle>
                    <SheetDescription>{[selected.business_name && selected.business_name !== selected.full_name ? selected.business_name : "", text(selected.category_name, "")].filter(Boolean).join(" · ")}<span className="block font-mono text-[11px]">{selected.id}</span></SheetDescription>
                  </div>
                </div>
              </SheetHeader>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className={cn("inline-flex rounded-full border border-border px-2.5 py-1 text-xs font-medium", BUCKET_TONE[bucketOf(selected)])} data-testid="pro-bucket">{BUCKET_LABEL[bucketOf(selected)]}</span>
                {selected.verification_status && <StatusBadge status={selected.verification_status} />}
                {selected.covers_gurgaon && <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 px-2.5 py-1 text-xs font-medium" data-testid="drawer-covers-ggn">Covers GGN</span>}
              </div>

              {/* Only the actions that make sense for where this professional is right now. */}
              <div className="mt-4 rounded-lg border border-border p-3 space-y-2" data-testid="pro-actions">
                <p className="text-sm font-semibold flex items-center gap-1.5"><Icons.Zap className="h-4 w-4 text-primary" />What you can do</p>
                {proCompletion(selected).missing.length > 0 && ["approval", "signing_up", "correction"].includes(bucketOf(selected)) && <p className="text-[11px] text-amber-800">Missing: {proCompletion(selected).missing.join(", ")}</p>}
                {bucketOf(selected) === "on_hold" && selected.hold_reason && <p className="text-[11px] text-muted-foreground">On hold: {selected.hold_reason}</p>}
                {selected.correction_note && ["correction", "rejected"].includes(bucketOf(selected)) && <p className="text-[11px] text-muted-foreground">Note sent: {selected.correction_note}</p>}
                <div className="flex flex-wrap gap-2">
                  {actions.filter((a) => !["suspend", "reinstate", "cluster", "trades"].includes(a.key) && a.eligible(selected) && canRun(a)).map((a) => {
                    const Ic = Icons[a.icon] || Icons.Zap;
                    return <Button key={a.key} size="sm" variant={a.key === "approve" || a.key === "release" ? "default" : "outline"} className={cn(a.danger && "text-red-700")} onClick={() => setOne({ action: a, pro: selected })} data-testid={`pro-action-${a.key}`}><Ic className="h-3.5 w-3.5 mr-1" />{a.label}</Button>;
                  })}
                  {!actions.some((a) => !["suspend", "reinstate", "cluster", "trades"].includes(a.key) && a.eligible(selected)) && <p className="text-xs text-muted-foreground">Nothing to do here — use Account access below to suspend or reinstate.</p>}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                {[["Phone", text(selected.phone)], ["City", text(selected.city)], ["Service area", areaOf(selected)], ["Experience", text(selected.years_experience) === "—" ? "—" : `${text(selected.years_experience)} yrs`], ["Indicative price", selected.indicative_price ? inr(selected.indicative_price) : "—"], ["Rating", `★ ${Number(selected.rating_average || 0).toFixed(1)} (${selected.review_count || 0}) · ${selected.completed_project_count || 0} jobs`], ["Joined", fmtDate(selected.created_at)], ["Created by", `${text(selected.created_by_role)} `]].map(([k, v]) => (
                  <div key={k} className="rounded-lg border border-border bg-secondary/50 p-3">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{k}</p>
                    <p className="text-sm text-foreground mt-1 break-words">{v}</p>
                  </div>
                ))}
              </div>

              <ProPhotos pro={selected} />

              <ProTrades pro={selected} procats={procats} categories={svcCats} />

              <HasTeamSwitch pro={selected} />

              <div className="mt-4 rounded-lg border border-border p-3">
                <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Cluster assignment</p>
                {clusters.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No clusters defined yet. Add them in Incentives & Clusters.</p>
                ) : (
                  <Select value={selected.cluster || ""} onValueChange={(v) => setCluster(selected, v)}>
                    <SelectTrigger data-testid="pro-cluster-select"><SelectValue placeholder="Assign a cluster" /></SelectTrigger>
                    <SelectContent>{clusters.map((c) => <SelectItem key={c.id || c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>

              {text(selected.about_me) !== "—" && (
                <div className="mt-4"><p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">About</p><p className="text-sm text-foreground">{text(selected.about_me)}</p></div>
              )}

              {selected.kyc && typeof selected.kyc === "object" && (
                <div className="mt-4">
                  <h4 className="font-heading font-semibold text-foreground mb-2 flex items-center gap-2 text-sm"><Icons.FileCheck className="h-4 w-4 text-primary" /> KYC</h4>
                  <div className="rounded-lg border border-border p-3 space-y-1.5 text-sm">
                    <KV k="ID type" v={text(selected.kyc.id_type)} />
                    <KV k="ID number" v={text(selected.kyc.id_number)} />
                    {kycUrls(selected.kyc).map((u, i) => <a key={u} href={u} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1 mr-3" data-testid={`pro-kyc-doc-${i}`}><Icons.ExternalLink className="h-3 w-3" /> ID document {i + 1}</a>)}
                  </div>
                </div>
              )}

              {Array.isArray(selected.portfolio) && selected.portfolio.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-heading font-semibold text-foreground mb-2 flex items-center gap-2 text-sm"><Icons.Images className="h-4 w-4 text-primary" /> Portfolio ({selected.portfolio.length})</h4>
                  <div className="grid grid-cols-3 gap-2">
                    {selected.portfolio.map((m, i) => { const url = typeof m === "string" ? m : m?.url; const isVid = /\.(mp4|mov|webm)/i.test(url || "") || m?.type === "video"; return (
                      <a key={i} href={url} target="_blank" rel="noreferrer" className="aspect-square rounded-lg border border-border overflow-hidden bg-secondary flex items-center justify-center">
                        {isVid ? <Icons.PlayCircle className="h-6 w-6 text-muted-foreground" /> : <img src={url} alt="" className="w-full h-full object-cover" />}
                      </a>
                    ); })}
                  </div>
                </div>
              )}

              <AccountSuspension kind="professional" entity={selected} uid={selected.user_id} phone={selected.phone} name={text(selected.full_name, "professional")} />
              <AllFieldsPanel path="professionals" id={selected.id} />
            </>
          )}
        </SheetContent>
      </Sheet>
      {one && <ActionDialog key={`${one.action.key}-${one.pro.id}`} action={one.action} docs={[one.pro]} nameOf={proName} onClose={() => setOne(null)} onDone={() => setOne(null)} />}
    </div>
  );
}

function Avatar({ url, name, big }) {
  const cls = big ? "h-14 w-14 rounded-xl text-base" : "h-9 w-9 rounded-full text-xs";
  if (url) return <img src={url} alt={text(name)} className={`${cls} object-cover bg-secondary`} />;
  return <div className={`${cls} bg-primary/10 text-primary flex items-center justify-center font-semibold`}>{initials(text(name, "P"))}</div>;
}
const KV = ({ k, v }) => <div className="flex justify-between gap-3"><span className="text-muted-foreground">{k}</span><span className="text-foreground text-right break-words">{v}</span></div>;
